import { rd } from '@passionware/monads';
import { createMatcher } from '@passionware/query-toolkit';
import { uniqBy } from 'lodash';
import { useWatch } from 'react-hook-form';
import type { ReactNode } from 'react';
import type { SelectOption } from 'v5/core/form-schema/formSchema.types';
import { OptionRef } from 'v5/core/entity-values/values.types';
import { createLocalId } from 'v5/core/form-schema/formRows';
import { ComboboxCreateItemQuery } from 'v5/design-system/Combobox';
import { FormField } from 'v5/design-system/Form';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectQueryRenderer,
  MultiSelectTrigger,
  MultiSelectTriggerProps
} from 'v5/design-system/MultiSelect';
import { useFieldContext } from '../FieldContext';
import { CreateFromQueryItem } from './CreateFromQueryItem';
import { EmptyField } from './EmptyField';
import { FieldControlBase } from './FieldControlBase';
import type { FieldFrameSpec } from './fieldComponent.types';

export type MultiSelectRenderValue = NonNullable<
  MultiSelectTriggerProps['renderValue']
>;

export type OptionRefMultiSelectProps<O extends SelectOption> = {
  spec: FieldFrameSpec & { options: O[] };
  path: string;
  hideLabel?: boolean;
  placeholder: string;
  /** `null` disables creating new options. */
  create: { label: string } | null;
  renderSelectedValue?: MultiSelectRenderValue;
  /** Content of an option's dropdown row; the label alone by default. A created, unsaved option shows its label. */
  renderOption?: (option: O) => ReactNode;
};

/** Combobox key of a ref: persisted id as string, or the local id. */
export function optionRefKey(ref: OptionRef): string {
  return ref.type === 'existing' ? String(ref.id) : ref.localId;
}

type NewOptionRef = Extract<OptionRef, { type: 'new' }>;

function isNewRef(ref: OptionRef): ref is NewOptionRef {
  return ref.type === 'new';
}

/**
 * Multi-select over `OptionRef[]`: selected keys are
 * `existing` ids as strings or `new` local ids; creating adds a `new` ref
 * whose label is shown in brand colour until it is persisted.
 */
export function OptionRefMultiSelect<O extends SelectOption>({
  spec,
  path,
  hideLabel,
  placeholder,
  create,
  renderSelectedValue,
  renderOption = option => option.label
}: OptionRefMultiSelectProps<O>) {
  const ctx = useFieldContext();
  const size = ctx.size === 'lg' ? 'md' : ctx.size;
  const readOnly = ctx.mode === 'display';
  const selected =
    useWatch<Record<string, OptionRef[] | undefined>>({ name: path }) ?? [];

  if (readOnly && selected.length === 0) {
    return <EmptyField spec={spec} path={path} hideLabel={hideLabel} />;
  }

  return (
    <FormField
      name={path}
      render={({ field: { onChange, ref, value } }) => {
        const refs: OptionRef[] = Array.isArray(value) ? value : [];
        const newRefs = new Map<string, NewOptionRef>(
          refs.filter(isNewRef).map(item => [item.localId, item])
        );
        // A created, unsaved option is only its label.
        const options = uniqBy(
          [
            ...spec.options.map(option => ({
              value: option.value,
              label: option.label,
              content: renderOption(option)
            })),
            ...refs.filter(isNewRef).map(item => ({
              value: item.localId,
              label: item.label,
              content: item.label
            }))
          ],
          option => option.value
        );
        const toRef = (key: string): OptionRef =>
          newRefs.get(key) ?? { type: 'existing', id: Number(key) };

        return (
          <FieldControlBase spec={spec} hideLabel={hideLabel}>
            <MultiSelect
              value={refs.map(optionRefKey)}
              onValueChange={keys => onChange(keys.map(toRef))}
            >
              <MultiSelectTrigger
                size={size}
                variant={ctx.variant}
                placeholder={placeholder}
                readOnly={readOnly}
                ref={ref}
                renderValue={renderSelectedValue}
              >
                {keys =>
                  rd.of(
                    keys.map(key => {
                      const option = options.find(o => o.value === key);
                      const label = option?.label ?? key;
                      return newRefs.has(key) ? (
                        <span className="text-brand-500 font-bold">
                          {label}
                        </span>
                      ) : (
                        label
                      );
                    })
                  )
                }
              </MultiSelectTrigger>
              <MultiSelectContent>
                <MultiSelectQueryRenderer>
                  {query =>
                    rd.of(
                      <MultiSelectGroup>
                        {options
                          .filter(createMatcher(query, o => o.label))
                          .map(option => (
                            <MultiSelectItem
                              key={option.value}
                              value={option.value}
                            >
                              {option.content}
                            </MultiSelectItem>
                          ))}
                      </MultiSelectGroup>
                    )
                  }
                </MultiSelectQueryRenderer>
                {create !== null && !readOnly && (
                  <CreateFromQueryItem
                    onSelect={label => {
                      // Names are unique regardless of case on the server: a
                      // typed name that already exists picks that option.
                      const typed = label.toLocaleLowerCase();
                      const match = options.find(
                        option =>
                          option.label.trim().toLocaleLowerCase() === typed
                      );
                      if (match === undefined) {
                        onChange([
                          ...refs,
                          { type: 'new', localId: createLocalId(), label }
                        ]);
                      } else if (
                        !refs.some(r => optionRefKey(r) === match.value)
                      ) {
                        onChange([...refs, toRef(match.value)]);
                      }
                    }}
                  >
                    {create.label}: <ComboboxCreateItemQuery />
                  </CreateFromQueryItem>
                )}
              </MultiSelectContent>
            </MultiSelect>
          </FieldControlBase>
        );
      }}
    />
  );
}
